import {useDiffEntries, useThreads} from '../stores/selectors'
import type {DiffEntry} from '@github-ui/diff-lines/types'
import type {Thread, StaticDiffLine, ThreadSubject} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'
import type {CommentPreviews} from '../components/toolbar/PreviewAuthors'
import type {ThreadPreview} from '../page-data/payloads/thread-previews'
import {useThreadPreviewPositioning} from '../page-data/loaders/use-thread-preview-positioning'

const DEFAULT_CONTEXT_LINES_TO_GRAB = 5

/**
 * Hook for getting pending review thread previews with positioning data stitched in
 * Returns PendingCommentPreviewType format directly to avoid type conversion
 */
export function useGetPendingThreadPreviewsFromThreads(pendingReviewIds?: number[]): ThreadPreview[] {
  const threads = useThreads(pendingReviewIds)
  const diffEntries = useDiffEntries()

  const {pendingPreviews, threadsWithoutContext} = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return {pendingPreviews: [], threadsWithoutContext: []}
    }

    const previews: ThreadPreview[] = []
    const threadsWithoutContextInt: number[] = []

    for (const thread of threads) {
      const {diffSubject, hasContext, threadPreviewComments} = buildThreadPreviewData(thread, diffEntries)

      if (!hasContext) {
        threadsWithoutContextInt.push(Number(thread.id))
      }

      // Get pending comments from this thread
      const pendingComments = (thread.commentsData?.comments ?? []).filter(comment => comment.state === 'pending')

      for (const currentComment of pendingComments) {
        previews.push({
          threadId: thread.id,
          commentId: currentComment.databaseId?.toString() || '',
          path: thread.positioning?.path || '',
          threadPreviewComments,
          firstComment: thread.commentsData?.comments[0] || undefined,
          isResolved: thread.isResolved,
          line: thread.line,
          subjectType: thread.subjectType,
          subject: diffSubject,
          commentsData: thread.commentsData,
          isOutdated: thread.isOutdated,
        })
      }
    }

    return {pendingPreviews: previews, threadsWithoutContext: threadsWithoutContextInt}
  }, [diffEntries, threads, pendingReviewIds])

  const threadPreviewPositioningMap = useThreadPreviewPositioning(threadsWithoutContext).data

  const stitchedPreviews = useMemo(() => {
    if (threadsWithoutContext.length === 0 || !threadPreviewPositioningMap) {
      return pendingPreviews
    }

    // Create new objects instead of mutating to trigger React re-renders
    return pendingPreviews.map(preview => {
      const threadId = Number(preview.threadId)

      const threadPreviewPositioning = threadPreviewPositioningMap[threadId]
      if (!threadPreviewPositioning) {
        return preview
      }

      return {
        ...preview,
        path: threadPreviewPositioning.path,
        subject: threadPreviewPositioning,
      }
    })
  }, [pendingPreviews, threadsWithoutContext, threadPreviewPositioningMap])

  return stitchedPreviews
}

/**
 * Builds the thread preview data for a given thread and diff entries
 */
function buildThreadPreviewData(
  thread: Thread,
  diffEntries: Record<string, DiffEntry>,
): {
  diffSubject: ThreadSubject | undefined
  hasContext: boolean
  threadPreviewComments: CommentPreviews[]
} {
  let diffSubject = thread.subject
  let hasContext = true

  if (thread.positioning) {
    const diffEntry = diffEntries[thread.positioning.path]
    const diffLines = [] as StaticDiffLine[]

    if (diffEntry && !thread.isOutdated) {
      const endLineIndex = diffEntry.diffLines.findIndex(line => {
        return (
          (thread.positioning?.endDiffSide === 'RIGHT' && line.right === thread.positioning?.endLine) ||
          (thread.positioning?.endDiffSide === 'LEFT' && line.left === thread.positioning?.endLine)
        )
      })

      if (endLineIndex === -1) {
        hasContext = false
      } else {
        let startLineIndex = Math.max(0, endLineIndex - DEFAULT_CONTEXT_LINES_TO_GRAB)

        if (
          thread.positioning?.startLine &&
          thread.positioning?.startDiffSide &&
          (thread.positioning.startLine !== thread.positioning.endLine ||
            thread.positioning.startDiffSide !== thread.positioning.endDiffSide)
        ) {
          startLineIndex = diffEntry.diffLines.findIndex(line => {
            return (
              (thread.positioning?.startDiffSide === 'RIGHT' && line.right === thread.positioning?.startLine) ||
              (thread.positioning?.startDiffSide === 'LEFT' && line.left === thread.positioning?.startLine)
            )
          })
        }

        for (let i = endLineIndex; i >= startLineIndex; i--) {
          const diffLine = diffEntry.diffLines[i]

          if (diffLine) {
            diffLines.unshift(diffLine as StaticDiffLine)

            if (diffLine.type === 'HUNK') {
              // End the preview if we hit a hunk line
              break
            }
          }
        }
      }

      diffSubject = {...thread.subject, diffLines}
    }
  } else {
    hasContext = false
  }

  const threadPreviewComments =
    thread.commentsData?.comments.slice(1).reduce((acc, comment) => {
      if (comment.author) {
        acc.push({
          author: {
            avatarUrl: comment.author.avatarUrl,
            login: comment.author.login,
          },
        })
      }
      return acc
    }, [] as CommentPreviews[]) || []

  return {diffSubject, hasContext, threadPreviewComments}
}
